import { collections, documentPreviews, filterOptions } from "./mockData";
import type {
  ChatRequest,
  ChatResponse,
  ClearInteractionLogsResponse,
  Collection,
  CopyTrainingExamplesResponse,
  DocumentPreview,
  FilterOptions,
  InteractionLogCreateInput,
  InteractionLogsResponse,
  InteractionLogUpdateInput,
  SearchFilters,
  SearchRequest,
  SearchResponse,
  SourceDocument,
} from "./types";

const PIN_STORAGE_KEY = "mydigigovsa-source-pins";
const SESSION_STORAGE_KEY = "mydigigovsa-source-session";

const delay = (ms = 180) => new Promise((resolve) => window.setTimeout(resolve, ms));

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      accept: "application/json",
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: unknown } | null;
    const detail = typeof payload?.error === "string" ? `: ${payload.error}` : "";
    throw new Error(`API request failed: ${response.status}${detail}`);
  }

  return response.json() as Promise<T>;
}

function readPinnedIds(): string[] {
  try {
    const raw = window.localStorage.getItem(PIN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writePinnedIds(ids: string[]) {
  window.localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify([...new Set(ids)]));
}

export function getInteractionSessionId(): string {
  try {
    const existing = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const nextId =
      window.crypto?.randomUUID?.() ??
      `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(SESSION_STORAGE_KEY, nextId);
    return nextId;
  } catch {
    return `session-${Date.now()}`;
  }
}

export function getClientContext(): Record<string, unknown> {
  return {
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    userAgent: window.navigator.userAgent,
    language: window.navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}

function toSourceDocument(preview: DocumentPreview): SourceDocument {
  const { keyFacts: _keyFacts, excerpts: _excerpts, relatedDocuments: _related, ...source } = preview;
  return source;
}

function matchesQuery(document: DocumentPreview, query: string): boolean {
  if (!query.trim()) {
    return true;
  }

  const haystack = [
    document.title,
    document.collectionName,
    document.type,
    document.department,
    document.excerpt,
    document.summary,
    document.tags.join(" "),
    document.sectionMatches.join(" "),
  ]
    .join(" ")
    .toLowerCase();

  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

function matchesFilters(document: DocumentPreview, filters: SearchFilters): boolean {
  const collectionMatch =
    filters.collections.length === 0 ||
    filters.collections.includes(document.collectionId);
  const typeMatch =
    filters.documentTypes.length === 0 ||
    filters.documentTypes.includes(document.type);
  const yearMatch =
    filters.years.length === 0 || filters.years.includes(document.year);
  const tagMatch =
    filters.tags.length === 0 ||
    filters.tags.some((tag) => document.tags.includes(tag));

  return collectionMatch && typeMatch && yearMatch && tagMatch;
}

function rankForQuery(document: DocumentPreview, query: string): number {
  if (!query.trim()) {
    return document.relevance;
  }

  const normalized = query.toLowerCase();
  const titleBoost = document.title.toLowerCase().includes(normalized) ? 12 : 0;
  const tagBoost = document.tags.some((tag) => normalized.includes(tag.toLowerCase()))
    ? 8
    : 0;
  return Math.min(99, document.relevance + titleBoost + tagBoost);
}

function refinementsFor(results: SourceDocument[]): string[] {
  const tags = new Map<string, number>();
  for (const result of results) {
    for (const tag of result.tags) {
      tags.set(tag, (tags.get(tag) ?? 0) + 1);
    }
  }

  return [...tags.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([tag]) => tag);
}

export async function getCollections(): Promise<Collection[]> {
  try {
    return await fetchJson<Collection[]>("/api/collections");
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }

  await delay(80);
  return collections;
}

export async function getFilters(): Promise<FilterOptions> {
  try {
    return await fetchJson<FilterOptions>("/api/filters");
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }

  await delay(80);
  return filterOptions;
}

export async function getSourcesForCollection(
  collectionId: string,
): Promise<SourceDocument[]> {
  try {
    return await fetchJson<SourceDocument[]>(
      `/api/sources?collection=${encodeURIComponent(collectionId)}`,
    );
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }

  await delay(80);
  return documentPreviews
    .filter((document) => document.collectionId === collectionId)
    .sort((a, b) => a.title.localeCompare(b.title))
    .map(toSourceDocument);
}

export async function searchDocuments(
  query: SearchRequest["query"],
  filters: SearchRequest["filters"],
  options: Pick<SearchRequest, "threshold" | "maxSources">,
): Promise<SearchResponse> {
  try {
    return await fetchJson<SearchResponse>("/api/search", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        query,
        filters,
        ...options,
        sessionId: getInteractionSessionId(),
        clientContext: getClientContext(),
      }),
    });
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }

  await delay();

  const results = documentPreviews
    .filter((document) => matchesQuery(document, query))
    .filter((document) => matchesFilters(document, filters))
    .map((document) => ({
      ...toSourceDocument(document),
      relevance: rankForQuery(document, query),
    }))
    .filter((document) => document.relevance >= options.threshold * 100)
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, options.maxSources);

  return {
    query,
    threshold: options.threshold,
    maxSources: options.maxSources,
    answer:
      results.length > 0
        ? `I found ${results.length} sample source${
            results.length === 1 ? "" : "s"
          } related to "${query}". Review the ranked snippets below before opening the full source documents.`
        : undefined,
    total: results.length,
    results,
    refinements: refinementsFor(results),
    mode: "local-mock",
  };
}

export async function sendChatMessage(
  input: Omit<ChatRequest, "sessionId" | "clientContext">,
): Promise<ChatResponse> {
  return fetchJson<ChatResponse>("/api/chat", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      ...input,
      sessionId: getInteractionSessionId(),
      clientContext: getClientContext(),
    }),
  });
}

export async function getDocumentPreview(
  documentId: string,
): Promise<DocumentPreview | null> {
  try {
    return await fetchJson<DocumentPreview>(
      `/api/documents/${encodeURIComponent(documentId)}/preview`,
    );
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }

  await delay(120);
  return documentPreviews.find((document) => document.id === documentId) ?? null;
}

export async function getPinnedSources(): Promise<SourceDocument[]> {
  const pinnedIds = readPinnedIds();
  try {
    const ids = pinnedIds.map(encodeURIComponent).join(",");
    return await fetchJson<SourceDocument[]>(`/api/pins?ids=${ids}`);
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }

  await delay(40);
  return documentPreviews
    .filter((document) => pinnedIds.includes(document.id))
    .map(toSourceDocument);
}

export async function pinSource(documentId: string): Promise<SourceDocument[]> {
  const ids = readPinnedIds();
  const nextIds = [...new Set([...ids, documentId])];
  writePinnedIds(nextIds);
  try {
    return await fetchJson<SourceDocument[]>("/api/pins", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({ documentId, pinnedIds: ids }),
    });
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }
  return getPinnedSources();
}

export async function unpinSource(documentId: string): Promise<SourceDocument[]> {
  const nextIds = readPinnedIds().filter((id) => id !== documentId);
  writePinnedIds(nextIds);
  try {
    return await fetchJson<SourceDocument[]>(
      `/api/pins/${encodeURIComponent(documentId)}`,
      {
        method: "DELETE",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({ pinnedIds: nextIds }),
      },
    );
  } catch {
    // Fallback keeps plain Vite local development usable without Pages Functions.
  }
  return getPinnedSources();
}

export async function recordInteractionLog(
  input: InteractionLogCreateInput,
): Promise<string | null> {
  try {
    const response = await fetchJson<{ id: string | null }>("/api/eval-logs", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        ...input,
        sessionId: input.sessionId ?? getInteractionSessionId(),
        clientContext: input.clientContext ?? getClientContext(),
      }),
    });
    return response.id;
  } catch {
    return null;
  }
}

export async function getInteractionLogs(
  password: string,
  filters: { eventType?: string; resultMode?: string; query?: string; limit?: number } = {},
): Promise<InteractionLogsResponse> {
  const params = new URLSearchParams();
  if (filters.eventType) params.set("eventType", filters.eventType);
  if (filters.resultMode) params.set("resultMode", filters.resultMode);
  if (filters.query) params.set("query", filters.query);
  if (filters.limit) params.set("limit", String(filters.limit));
  const query = params.toString();
  return fetchJson<InteractionLogsResponse>(`/api/eval-logs${query ? `?${query}` : ""}`, {
    headers: logAccessHeaders(password),
  });
}

export async function updateInteractionLog(
  password: string,
  id: string,
  input: InteractionLogUpdateInput,
): Promise<InteractionLogsResponse["logs"][number]> {
  const response = await fetchJson<{ log: InteractionLogsResponse["logs"][number] }>(
    `/api/eval-logs/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: {
        "content-type": "application/json",
        ...logAccessHeaders(password),
      },
      body: JSON.stringify(input),
    },
  );
  return response.log;
}

export async function copyLogsToTrainingExamples(
  password: string,
  ids: string[],
): Promise<CopyTrainingExamplesResponse> {
  return fetchJson<CopyTrainingExamplesResponse>("/api/eval-logs/training-examples", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...logAccessHeaders(password),
    },
    body: JSON.stringify({ ids }),
  });
}

export async function clearInteractionLogs(
  password: string,
): Promise<ClearInteractionLogsResponse> {
  return fetchJson<ClearInteractionLogsResponse>("/api/eval-logs", {
    method: "DELETE",
    headers: logAccessHeaders(password),
  });
}

function logAccessHeaders(password: string): HeadersInit {
  return {
    "x-eval-log-password": password,
  };
}
